import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../api/client';
import PageTemplate from './PageTemplate';
import './ModulePage.css';

function Teachers() {
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [teachers, setTeachers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState('');
  const [deletingId, setDeletingId] = useState(null);

  const fetchTeachers = async () => {
    setListLoading(true);
    setListError('');
    try {
      const [teachersResponse, coursesResponse] = await Promise.all([
        apiClient.get('/api/auth/teachers/'),
        apiClient.get('/api/courses/'),
      ]);

      const teachersData = teachersResponse.data;
      const teachersResults = Array.isArray(teachersData)
        ? teachersData
        : teachersData?.teachers?.results ||
          teachersData?.teachers ||
          teachersData?.results ||
          [];
      setTeachers(Array.isArray(teachersResults) ? teachersResults : []);

      const coursesData = coursesResponse.data;
      const coursesResults = Array.isArray(coursesData)
        ? coursesData
        : coursesData?.courses?.results || coursesData?.courses || coursesData?.results || [];
      setCourses(Array.isArray(coursesResults) ? coursesResults : []);
    } catch (err) {
      setListError('Unable to fetch teachers. Please try again.');
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    fetchTeachers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredTeachers = teachers.filter((teacher) => {
    const term = search.toLowerCase();
    if (!term) return true;
    return (
      teacher.full_name.toLowerCase().includes(term) ||
      teacher.email.toLowerCase().includes(term) ||
      (teacher.course_name || '').toLowerCase().includes(term)
    );
  });

  const getTeacherDisplayName = (teacher) => {
    const teacherName = teacher?.full_name || teacher?.name || 'Unknown teacher';
    const courseName =
      teacher?.course_name ||
      teacher?.assigned_course_name ||
      teacher?.course?.course_name ||
      teacher?.course?.name ||
      '';

    return courseName ? `${teacherName} (${courseName})` : teacherName;
  };

  const handleEdit = (teacherId) => {
    navigate(`/teachers/edit/${teacherId}`);
  };

  const handleDelete = async (teacherId, teacherName) => {
    if (
      !window.confirm(
        `Are you sure you want to delete "${teacherName}"? This action cannot be undone.`
      )
    ) {
      return;
    }

    setDeletingId(teacherId);
    try {
      await apiClient.delete(`/api/auth/teachers/${teacherId}/`);
      setTeachers((prev) =>
        prev.filter((t) => String(t.teacher_id || t.id) !== String(teacherId))
      );
      setListError('');
    } catch (err) {
      setListError(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          'Unable to delete teacher. Please try again.'
      );
    } finally {
      setDeletingId(null);
    }
  };

  const getTeacherCourseName = (teacher) => {
    const directCourseName =
      teacher?.course_name ||
      teacher?.assigned_course_name ||
      teacher?.course?.course_name ||
      teacher?.course?.name ||
      '';
    if (directCourseName) return directCourseName;

    const teacherCourseId =
      teacher?.course_id ||
      teacher?.assigned_course ||
      teacher?.course?.course_id ||
      teacher?.course?.id ||
      teacher?.course;

    if (!teacherCourseId) return '';

    const matchedCourse = courses.find((course) => {
      const courseId = course?.course_id || course?.id;
      return String(courseId || '') === String(teacherCourseId);
    });

    return matchedCourse?.course_name || matchedCourse?.name || '';
  };

  return (
    <PageTemplate
      title="Teacher Management"
      subtitle="View existing teachers and add new ones"
    >
      <div className="teachers-page">
        <div className="module-actions">
          <input
            type="search"
            placeholder="Search by name, email, or course..."
            className="search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button
            type="button"
            className="btn-primary"
            onClick={() => navigate('/teachers/add')}
          >
            Add Teacher
          </button>
        </div>

        <div className="teachers-table-wrapper" role="region" aria-label="Teachers table">
          <table className="teachers-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Course</th>
                <th className="table-th-center">Role</th>
                <th className="actions-cell table-th-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {listLoading ? (
                <tr>
                  <td colSpan="6" className="teachers-empty teachers-empty--loading">
                    Loading teachers…
                  </td>
                </tr>
              ) : listError ? (
                <tr>
                  <td colSpan="6" className="teachers-empty">
                    {listError}
                  </td>
                </tr>
              ) : filteredTeachers.length === 0 ? (
                <tr>
                  <td colSpan="6" className="teachers-empty">
                    No teachers found.
                  </td>
                </tr>
              ) : (
                filteredTeachers.map((teacher) => {
                  const teacherId = teacher.teacher_id || teacher.id;
                  const teacherName = teacher.full_name || teacher.name || 'Teacher';
                  return (
                    <tr key={teacherId}>
                      <td className="table-cell-strong">{getTeacherDisplayName(teacher)}</td>
                      <td>{teacher.email}</td>
                      <td>{teacher.phone_number}</td>
                      <td>{getTeacherCourseName(teacher) || '—'}</td>
                      <td className="table-td-center">
                        <span className="batch-type-pill">{teacher.role}</span>
                      </td>
                      <td className="actions-cell table-td-center">
                        <div className="table-actions">
                          <button
                            type="button"
                            className="table-btn table-btn--primary"
                            onClick={() => handleEdit(teacherId)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="table-btn table-btn--danger"
                            onClick={() => handleDelete(teacherId, teacherName)}
                            disabled={deletingId === teacherId}
                          >
                            {deletingId === teacherId ? 'Deleting...' : 'Delete'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </PageTemplate>
  );
}

export default Teachers;


